import React, { useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import FormatCurrency from 'components/Common/FormatCurrency'
import Image from 'components/Common/Image/Image'
import GeoContext from 'contexts/geoContext'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { convertAudToRegion } from 'luxPlus/lib/getRegionSaveAmountCopy'
import type { MemberSaving } from './luxPlusMemberSaving.types'

const MOBILE_IMAGE_SIZE = 80
const DESKTOP_IMAGE_SIZE = 103

const MemberSavingImageWrapper = styled.div`
  width: ${rem(MOBILE_IMAGE_SIZE)};
  height: ${rem(MOBILE_IMAGE_SIZE)};
  flex-shrink: 0;
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    width: ${rem(DESKTOP_IMAGE_SIZE)};
    height: ${rem(DESKTOP_IMAGE_SIZE)};
  }
`

interface Props {
  saving: MemberSaving
}

function LuxPlusMemberSavingCard({ saving }: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const localSaving = convertAudToRegion(saving.savedAmountAud, currentRegionCode)
  const destinationLabel = intl.formatMessage(saving.destination)

  const memberBookedText = intl.formatMessage({
    id: '6OD0dvKmDCJAQ0A8yWRw',
    defaultMessage: '<strong>A member</strong> booked {nights} nights in {destination}',
    description: 'Notification card text showing a member booked a trip with destination and duration',
  }, {
    nights: saving.nights,
    destination: destinationLabel,
    strong: (chunks) => <strong>{chunks}</strong>,
  })

  const savingsText = intl.formatMessage({
    id: 'KQSi7VR3iL757xbSZbo1',
    defaultMessage: 'Saved {amount} with {programName}',
    description: 'Notification card text showing amount saved with LuxPlus membership',
  }, {
    amount: <FormatCurrency value={localSaving} format="rounded" />,
    programName: LUXURY_PLUS.PROGRAM_NAME,
  })

  return <>
    <MemberSavingImageWrapper>
      <Image id={saving.imageId} fit="center" dpr={2} alt="" />
    </MemberSavingImageWrapper>

    <CSSBreakpoint max="mobile">
      <Group direction="vertical" gap={4}>
        <BodyText variant="medium">{memberBookedText}</BodyText>
        <BodyText variant="medium" colour="highlight-primary-dark" weight="bold">{savingsText}</BodyText>
      </Group>
    </CSSBreakpoint>

    <CSSBreakpoint min="tablet">
      <Group direction="vertical" gap={4}>
        <BodyText variant="large">{memberBookedText}</BodyText>
        <BodyText variant="large" colour="highlight-primary-dark" weight="bold">{savingsText}</BodyText>
      </Group>
    </CSSBreakpoint>
  </>
}

export default LuxPlusMemberSavingCard
